import * as React from 'react';

import { AssistantSidebarContent } from '@rematter/modules/assistant';
import { ChatSidebarContent } from '@rematter/modules/chat';
import { Button, cn } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { LucideX } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { ErrorBoundary } from '../components/error/ErrorBoundary';
import { Sidebar } from '../components/Sidebar';
import { Topbar } from '../components/Topbar';

import { useAssistantSidebar } from '~/app/contexts/RightSidebarContext';
import {
  $rightSidebarType,
  $rightSidebarWidth,
} from '~/app/state/rightSidebar.state';
import { useVersionMismatchNotification } from '~/shared/hooks/useVersionMismatchNotification';

export type AppLayoutProps = { children: React.ReactNode };

export const AppLayout = ({ children }: AppLayoutProps) => {
  // Monitor version mismatch between frontend and backend
  useVersionMismatchNotification();

  const [sidebarType, _setSidebarType] = useAtom($rightSidebarType);
  const [sidebarWidth, setSidebarWidth] = useAtom($rightSidebarWidth);
  const { isAssistantOpen, toggleAssistant } = useAssistantSidebar();

  const [isResizing, setIsResizing] = React.useState(false);

  const isOpen = sidebarType !== 'none';

  // Calculate dynamic constraints for the shared right sidebar (in pixels)
  // Since both chat and assistant share the same width, use unified constraints
  const calculateSidebarConstraints = React.useCallback(() => {
    if (sidebarType === 'none') {
      return { minSidebarWidth: 0, maxSidebarWidth: 0 };
    }

    const windowWidth = window.innerWidth;

    const minSidebarWidthPx = 600;
    const maxSidebarWidthPx = windowWidth * 0.8;

    return {
      minSidebarWidth: minSidebarWidthPx,
      maxSidebarWidth: maxSidebarWidthPx,
    };
  }, [sidebarType]);

  // Update shared width
  const updateWidth = React.useCallback(
    (newWidth: number) => {
      if (sidebarType === 'none') return;
      setSidebarWidth(newWidth);
    },
    [sidebarType, setSidebarWidth],
  );

  // Resize handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const constraints = calculateSidebarConstraints();
      const windowWidth = window.innerWidth;
      const mouseX = e.clientX;
      const newWidthPx = windowWidth - mouseX;
      const clampedWidth = Math.min(
        Math.max(newWidthPx, constraints.minSidebarWidth),
        constraints.maxSidebarWidth,
      );

      updateWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, calculateSidebarConstraints, updateWidth]);

  return (
    <div className="@AppLayout flex h-screen w-screen overflow-hidden bg-neutral/5">
      <Sidebar />
      <main className="flex flex-1 flex-col overflow-hidden">
        <Topbar />
        <div className="flex min-h-0 flex-1">
          <div className="isolate flex-1 overflow-auto">
            <ErrorBoundary>{children}</ErrorBoundary>
          </div>

          {/* Single Right Sidebar Container */}
          <div className="@RightSidebarContainer contents">
            {/* Resize Handle */}
            <div
              className={cn(
                'fixed top-14 bottom-0 z-50 w-1 cursor-col-resize bg-transparent transition-colors duration-200 hover:bg-primary/50',
                isResizing ? 'bg-primary/50' : '',
                !isOpen && 'pointer-events-none opacity-0',
              )}
              style={{ right: `${sidebarWidth}px` }}
              onMouseDown={handleMouseDown}
            >
              <div className="absolute -inset-x-2 inset-y-0 hover:bg-primary/5" />
              {isResizing && (
                <div className="absolute -inset-x-1 inset-y-0 bg-primary/5" />
              )}
            </div>

            {/* Sidebar Content */}
            <div
              className={cn(
                'fixed top-14 bottom-0 z-40 flex flex-col border-l border-neutral/20 bg-white',
                !isResizing && 'transition-transform duration-300 ease-in-out',
                isOpen ? 'translate-x-0' : 'translate-x-full',
              )}
              style={{
                right: 0,
                width: `${sidebarWidth}px`,
              }}
            >
              {sidebarType === 'chat' && <ChatSidebarContent />}
              {sidebarType === 'assistant' && <AssistantSidebarContent />}
            </div>
          </div>
        </div>

        {/* Floating AI Assistant Button */}
        <AnimatePresence>
          {isAssistantOpen && (
            <motion.div
              key="assistant-close-btn"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed bottom-6 z-50"
              style={{ right: `${sidebarWidth + 24}px` }}
            >
              <Button
                onPress={toggleAssistant}
                size="large"
                color="purple"
                className="size-14 overflow-hidden rounded-full shadow-md transition-all duration-300 ease-in-out hover:scale-105"
              >
                <LucideX size={24} />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};
